/**
 * ExportPlanDialog
 *
 * Excel-Export der Einsatzplanung. Standard-Auswahl: aktuelle Woche +
 * folgende Woche. Per KW-Picker lassen sich beliebige Wochen waehlen,
 * oder ein freier Datumsbereich.
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addWeeks, getISOWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Download, FileSpreadsheet, Calendar as CalendarIcon } from 'lucide-react';
import { shiftsAPI } from '../../lib/api';

/**
 * Liefert die naechsten N Wochen ab heute (oder ab `from`) als
 * `{value, label, start, end}` fuer einen Select.
 */
function buildWeekOptions(weeksAhead = 8, weeksBack = 4) {
  const today = new Date();
  const options = [];
  for (let i = -weeksBack; i <= weeksAhead; i += 1) {
    const ref = addWeeks(today, i);
    const start = startOfWeek(ref, { weekStartsOn: 1 });
    const end = endOfWeek(ref, { weekStartsOn: 1 });
    const kw = getISOWeek(start);
    const year = start.getFullYear();
    options.push({
      value: format(start, 'yyyy-MM-dd'),
      kw,
      year,
      label: `KW ${kw}/${year} · ${format(start, 'dd.MM.', { locale: de })} – ${format(end, 'dd.MM.yyyy', { locale: de })}`,
      start,
      end,
    });
  }
  return options;
}

export default function ExportPlanDialog({ open, onOpenChange, locationId, locationName }) {
  // Standard: aktuelle Woche
  const today = new Date();
  const thisMonday = startOfWeek(today, { weekStartsOn: 1 });
  const nextMonday = addWeeks(thisMonday, 1);
  const nextSunday = endOfWeek(nextMonday, { weekStartsOn: 1 });

  const [mode, setMode] = useState('kw'); // "kw" | "range"
  const [weekStartIso, setWeekStartIso] = useState(format(thisMonday, 'yyyy-MM-dd'));
  const [weekCount, setWeekCount] = useState(2); // Standard: 2 Wochen (aktuelle + folgende)
  const [rangeStart, setRangeStart] = useState(format(thisMonday, 'yyyy-MM-dd'));
  const [rangeEnd, setRangeEnd] = useState(format(nextSunday, 'yyyy-MM-dd'));
  const [busy, setBusy] = useState(false);

  const weekOptions = useMemo(() => buildWeekOptions(12, 4), []);

  // Berechne effektive Start/Enddaten je nach Mode
  const effective = useMemo(() => {
    if (mode === 'kw') {
      const start = weekOptions.find((w) => w.value === weekStartIso)?.start || thisMonday;
      const lastWeekStart = addWeeks(start, Math.max(1, weekCount) - 1);
      const end = endOfWeek(lastWeekStart, { weekStartsOn: 1 });
      return {
        start_date: format(start, 'yyyy-MM-dd'),
        end_date: format(end, 'yyyy-MM-dd'),
      };
    }
    return { start_date: rangeStart, end_date: rangeEnd };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, weekStartIso, weekCount, rangeStart, rangeEnd]);

  const downloadName = `einsatzplan_${effective.start_date}_${effective.end_date}.xlsx`;

  const handleExport = async () => {
    if (!effective.start_date || !effective.end_date) {
      toast.error('Bitte Zeitraum waehlen');
      return;
    }
    if (effective.end_date < effective.start_date) {
      toast.error('Enddatum vor Startdatum');
      return;
    }
    setBusy(true);
    try {
      const params = { start_date: effective.start_date, end_date: effective.end_date };
      if (locationId && locationId !== 'all') params.location_id = locationId;
      const res = await shiftsAPI.exportPlan(params);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel-Export erfolgreich erstellt');
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Export fehlgeschlagen';
      toast.error(typeof msg === 'string' ? msg : 'Export fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-4 sm:p-6" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Einsatzplan exportieren
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {locationName ? `Standort: ${locationName}` : 'Alle erreichbaren Standorte'} · Excel (.xlsx) mit 5 Sheets
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="kw" data-testid="export-mode-kw">
                Nach Kalenderwoche
              </TabsTrigger>
              <TabsTrigger value="range" data-testid="export-mode-range">
                Datumsbereich
              </TabsTrigger>
            </TabsList>

            <TabsContent value="kw" className="space-y-3 mt-3">
              <div>
                <Label className="text-xs text-muted-foreground">Ab Kalenderwoche</Label>
                <Select value={weekStartIso} onValueChange={setWeekStartIso}>
                  <SelectTrigger data-testid="export-kw-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {weekOptions.map((w) => (
                      <SelectItem key={w.value} value={w.value}>
                        {w.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Anzahl Wochen</Label>
                <Select
                  value={String(weekCount)}
                  onValueChange={(v) => setWeekCount(Number(v))}
                >
                  <SelectTrigger data-testid="export-kw-count">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 8, 12].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} {n === 1 ? 'Woche' : 'Wochen'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="range" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Von</Label>
                  <Input
                    type="date"
                    value={rangeStart}
                    onChange={(e) => setRangeStart(e.target.value)}
                    data-testid="export-range-from"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Bis</Label>
                  <Input
                    type="date"
                    value={rangeEnd}
                    onChange={(e) => setRangeEnd(e.target.value)}
                    data-testid="export-range-to"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-700">
            <div className="flex items-center gap-2 font-medium mb-1">
              <CalendarIcon className="h-3.5 w-3.5" />
              Zeitraum: {effective.start_date} – {effective.end_date}
            </div>
            <div className="text-stone-600 leading-relaxed">
              Workbook enthaelt 5 Sheets: <strong>Wochenraster</strong> (wie TV-Ansicht),
              <strong> Einsaetze</strong> (Liste), <strong>Aktivitaeten</strong> (Liste),
              <strong> Massnahmen-Uebersicht</strong> und <strong>Mitarbeiter-Summen</strong>.
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            className="w-full sm:w-auto"
            onClick={handleExport}
            disabled={busy}
            data-testid="export-plan-download-btn"
          >
            {busy ? (
              <span className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Erstelle Datei…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Excel herunterladen
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
